/**
 * @module app.shared
 */

import { IApiSessionState, IApiSessionLogin } from 'app/shared/lib/api/api-session-types';

export type QueryModelState = 'inactive' | 'inactiveOnly' | 'active';
export type QuerySortDirection = 'ascending' | 'descending';
export type QuerySortNulls = 'default' | 'first' | 'last';
export type QueryLimit = 'none' | 'all';

export type QueryEqualityOperator = 'eq' | 'ne';
export type QueryComparisonOperator = QueryEqualityOperator | 'gt' | 'lt' | 'gte' | 'lte';
export type QueryStringComparisonOperator = QueryComparisonOperator | 'startsWith' | 'contains' | 'endsWith' | 'notStartsWith' | 'notContains' | 'notEndsWith';
export type QueryFilterGroupOperator = 'and' | 'or';

export type QuerySpecialDate = 'today';
export type QuerySpecialDateTime = 'now';

export type QuerySessionValue = 'user.id' | 'application.id' | '$context.id';

export interface IApiStandardLoadOptionsApplyOptions {
    applyPaginationSizeToDetails: boolean
}

export interface IQuerySortGroup {
    reference: string;
    matchValue: string;
}

// TODO: remove nulls in favor of optionals here

export interface IQuerySort {
    reference: string;
    direction?: QuerySortDirection | null;
    nulls?: QuerySortNulls | null;
}

// TODO: remove nulls in favor of optionals here

export interface IQueryInclude {
    reference: string,
    state?: QueryModelState | null
}

export interface IQuerySortingOptions {
    entries: IQuerySort[]
    groupEntries: IQuerySortGroup[]
}

export interface IQueryPaginationOptions {
    size: number;
    count: number | null;
    index: number;
}

export interface IQueryLoadOptions {
    all: boolean;
    skip: number | null;
    top: number | null;
    page: number | null;
    includeInactive: boolean;
    sorting: IQuerySortingOptions;
    // TODO: Remove this
    pagination: IQueryPaginationOptions;
}

export interface IQueryOperation {
    buildExpression(...encodedOperands: string[]): string;
}

export type QueryOperationType = 'infix' | 'function';

export class QueryOperation implements IQueryOperation {

    constructor(public readonly name: string, public readonly type: QueryOperationType) { }

    public buildExpression(...encodedOperands: string[]): string {
        if(this.type === 'function') {
            const argumentsList = encodedOperands.join(',');
            return `${this.name}(${argumentsList})`;
        }
        return encodedOperands.join(`+${this.name}+`);
    }

    public negate(shouldNegate: boolean = false): IQueryOperation {
        return shouldNegate ? new LogicalNotQueryOperation(this) : this;
    }

}

export class LogicalNotQueryOperation implements IQueryOperation {

    constructor(public readonly operation: IQueryOperation) { }

    public buildExpression(...encodedOperands: string[]): string {
        const baseResult = this.operation.buildExpression(...encodedOperands);
        return `not(${baseResult})`;
    }

}

export class ApiError extends Error {

    public readonly httpStatusCode: number;
    public readonly method: string;
    public readonly url: string;
    public readonly status: IApiStatus;

    constructor(message: string, httpStatusCode?: number, method?: string, url?: string, status?: IApiStatus) {
        super(message);
        Object.setPrototypeOf(this, ApiError.prototype);

        this.httpStatusCode = httpStatusCode || 0;
        this.method = method || '';
        this.url = url || '';
        this.status = status || { errors: [], warnings: [] };
    }

    public get description(): string {
        if(this.status.errors.length > 0) {
            return this.status.errors[0].description;
        }
        else if(this.status.warnings.length > 0) {
            return this.status.warnings[0].description;
        }
        return this.message;
    }

    public hasError(...codes: string[]): boolean {
        return this.hasCode(codes, this.status.errors);
    }

    public hasWarning(...codes: string[]): boolean {
        return this.hasCode(codes, this.status.warnings);
    }

    private hasCode(codes: string[], list: IApiError[]): boolean {
        for(const item of list) {
            if(codes.find(code => code.toUpperCase() === (item.code || '').toUpperCase())) {
                return true;
            }
        }
        return false;
    }

}

export interface IApiError {
    code: string,
    description: string,
    property: string | null
}

export interface IApiStatus {
    warnings: IApiError[],
    errors: IApiError[]
}

export interface IApiResults {
    $baseUrl: string | null;
    isSuccessful: boolean;
    status: IApiStatus | null;
    timestamp: Date | null;
}

export interface IApiLink {
    rel: string;
    href: string;
    method: string;
    title?: string;
}

export interface IApiTokenResult {
    value?: string | null;
    isExpired?: boolean | null;
    expirationSeconds?: number | null;
}

export interface IApiOptions {
    logErrorResults: boolean;
    authenticate: boolean;
    skipRefresh: boolean;
    applicationEventCode: string | null;
}

export interface IApiBatchProgress {
    step(step: number, total: number, description: string): void;
    dismiss(): void;
}

export interface IApiBatchOptions extends IApiOptions {
    progress?: IApiBatchProgress;
}

export type ApiResponseType = 'json' | 'text' | 'blob';

export type HttpMethod = 'get' | 'put' | 'post' | 'delete';

export interface IRestApi {
    get(action: IApiRequest, options?: Partial<IApiOptions>): Promise<any>;
    put(action: IApiRequest, options?: Partial<IApiOptions>): Promise<any>;
    post(action: IApiRequest, options?: Partial<IApiOptions>): Promise<any>;
    delete(action: IApiRequest, options?: Partial<IApiOptions>): Promise<any>;
}

export interface IApiBatch {
    execute(api: IRestApi, options: IApiBatchOptions): Promise<any[]>
}

export interface IApiRequest {
    hasTenant(): boolean;
    withTenant(tenant: string): this;
    toString(): string;
    body?: any;
    responseType?: ApiResponseType;
}

export interface IApiQueryResults<T> extends IApiResults {
    count: number | null;
    resultsType: string;
    results: T[];
    links: IApiLink[] | null;
}

export interface IApiActionResults extends IApiResults {
    id: string | null;
    action: string | null;
    result?: any;
    resultText: string | null;
    model?: string | null;
}

export interface IApiSettings {
    $type?: '$settings';
    [key: string]: ApiSetting | undefined;
}

export interface IApiModelSettings extends IApiSettings {
    $model: string;
    $url: string;
    $extends?: string[];
}

export interface IApiRelationshipSettings extends IApiModelSettings {
    $rel: 'lookup' | 'details';
    $allowNull?: boolean;
    $targets?: string[];
}

export interface IApiPropertySettings extends IApiSettings {
    $allowDuplicates?: boolean;
    $allowNull?: boolean;
    $isEnabled?: boolean;
    $isVisible?: boolean;
    $maximumLength?: number;
    $numericPrecision?: number;
    $numericScale: number;
}

export type ApiSetting = IApiSettings | string | number | boolean | IApiSettings[] | string[] | number[] | boolean[] | null;

export interface IApiSettingsResults extends IApiResults {
    id: string | null;
    settings: IApiSettings;
}

export type ApiDownloadDisposition = 'attachment' | 'inline';

export type ApiUploadEncoding = 'utf-8' | 'ascii' | 'base64' | 'hex';

export interface IApiUploadFile {
    name: string;
    contentType?: string;
}

export interface IApiUploadContents {
    encoding: ApiUploadEncoding;
    data: string[];
}

export interface IApiUploadToken {
    value: string;
}

export interface IApiUploadRequest {
    isCanceled?: true;
    token?: IApiUploadToken;
    file?: IApiUploadFile;
    contents?: IApiUploadContents;
}

export interface IApiUploadedBytes {
    previous?: number | null;
    total?: number | null;
    maximum?: number | null;
}

export interface IApiUploadResults extends IApiResults {
    model: string | null;
    isCanceled: boolean | null;
    bytes: IApiUploadedBytes | null;
    token: IApiTokenResult | null;
}

export interface IApiAuthenticationToken {
    refresh: IApiTokenResult | null;
    access: IApiTokenResult | null;
}

export type ApiPasswordState =
    'unknown' |
    'valid' |
    'expiresSoon' |
    'expired';

export interface IApiAuthenticationPassword {
    state?: ApiPasswordState;
    expirationDate?: string | null;
    expirationWarningDate?: string | null;
}

export type ApiAuthenticationState =
    'unauthenticated' |
    'checkInRequired' |
    'checkInAvailable' |
    'authenticated' |
    'reauthenticated' |
    'deauthenticated' |
    'expired' |
    'twoFactorRequired' |
    'unknown';

export interface IApiAuthenticationResults extends IApiResults {
    password?: IApiAuthenticationPassword;
    session?: IApiSessionState;
    state?: ApiAuthenticationState;
    token?: IApiAuthenticationToken | null;
    logins?: IApiSessionLogin[];
    result?: any;
}

export enum ActionOptions {
    Insert = 'insert',
    Update = 'update',
    Delete = 'delete',
    Deactivate = 'deactivate',
    Reactivate = 'reactivate',
    Save = 'save'
}

export enum MethodOptions {
    Delete = 'delete',
    Get = 'get',
    Post = 'post',
    Put = 'put'
}

export interface IActionInfo {
    id?: string | null;
    action: ActionOptions;
    method: MethodOptions;
    url: string;
    body?: { [key: string]: any };
    reason?: string | null;
}

export interface IApiCustomBatch {
    calls: IActionInfo[];
    progress?: IApiBatchProgress;
}
